import React, { useRef, useEffect, useCallback, useMemo } from "react";
import PolygonMap from "../../PlolygonServices/PolygonMapService/PolygonMap";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { isEmptyOrNull } from "../../../utils/DataUtils";
import { googleApiCode } from "../../../config/constants";

// Define libraries outside component to prevent useJsApiLoader from reinitializing
const GOOGLE_MAP_LIBRARIES = ["geometry", "drawing", "places"];

// Default map container style
const DEFAULT_CONTAINER_STYLE = { height: "100%", width: "100%" };

const CustomGoogleMap = (props) => {
  const {
    refMap,
    defaultCenter,
    defaultZoom,
    onIdle,
    markers,
    polygons,
    handlePolygonClick,
    posZoneMarkerInfoWindow,
    openPolygonPosZoneInfoWindow,
    mapOptionStyles,
  } = props;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleApiCode,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const mapRef = useRef(null);

  // Memoize options to prevent unnecessary re-renders
  const mapOptions = useMemo(() => ({ 
    styles: mapOptionStyles ?? [] 
  }), [mapOptionStyles]);

  // Memoize onLoad callback
  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (defaultCenter) {
      map.setCenter(defaultCenter);
    }
    if (defaultZoom) {
      map.setZoom(defaultZoom);
    }

    if (refMap) {
      if (typeof refMap === 'function') {
        refMap(map);
      } else if (refMap && typeof refMap === 'object') {
        refMap.current = map;
      }
    }
  }, [defaultCenter, defaultZoom, refMap]);

  useEffect(() => {
    if (mapRef.current && defaultCenter) {
      mapRef.current.panTo(defaultCenter);
    }
  }, [defaultCenter]);

  useEffect(() => {
    if (mapRef.current && defaultZoom) {
      mapRef.current.setZoom(defaultZoom);
    }
  }, [defaultZoom]);

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  if (!isLoaded) {
    return <div style={{ height: "100%" }}>Loading...</div>;
  }

  return (
    <GoogleMap
      onLoad={handleMapLoad}
      onIdle={onIdle}
      options={mapOptions}
      mapContainerStyle={DEFAULT_CONTAINER_STYLE}
    >
      <>
        {!isEmptyOrNull(markers) && markers?.map((marker, markerIndex) =>
          marker?.visible && (
            <Marker
              key={`${marker?.key ?? ""}`}
              title={marker?.name ?? ""}
              icon={marker?.icon ?? ""}
              label={marker?.label ?? ""}
              position={marker?.location ?? ""}
              onClick={() => openPolygonPosZoneInfoWindow(markerIndex)}
            >
              {marker?.openWindow && (
                <InfoWindow
                    key={`infowindow-${marker?.posName ?? "InfoWindow"}`}
                    position={marker?.location}
                >
                    {posZoneMarkerInfoWindow?.(marker)}
                </InfoWindow>
              )}
            </Marker>
          )
        )}
        {!isEmptyOrNull(polygons) && polygons?.map((polygon) => (
          <PolygonMap
            key={polygon?.polygonKey ?? ""}
            name={`${polygon.polygonKey ?? ""}`}
            path={polygon.path}
            fillColor={polygon?.options?.fillColor}
            strokeColor={polygon?.options?.strokeColor}
            defaultOptions={polygon.options}
            onClick={(event) => handlePolygonClick(event, polygon)}
          />
        ))}
      </>
    </GoogleMap>
  );
};

export default CustomGoogleMap;
